import { useEffect, useState } from 'react'
import { FlaskConical, Stethoscope, Wrench, ExternalLink, AlertTriangle } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { labStageScope } from '../../lib/role.js'
import { Badge, fmtDateText } from '../../kit'
import {
  buildApptLabIndex, labsForAppt, carryOverLabsForAppt, labChaseState, CHASE_TONES,
  labTitle, labVendorName, labDoctorCode, labDoctorImg, labDue,
  shownStage, stageStyle, labStageName, labStageColor, hasRetainer, retainerSummary,
} from '../../lib/lab.js'

const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))

/**
 * งาน Lab — the lab pieces around ONE appointment, read out inside ApptEditDrawer
 * (Ram, 2026-10-20). The flask flag on the table / calendar says THAT there is lab
 * work; this says WHAT: the piece, its stage, the bench, and whether it will be in.
 *
 * Same index and the same two rules as ApptLabFlag (lib/lab.js), so the drawer can
 * never disagree with the flag that made someone open it:
 *   • this visit's own pieces — `labsForAppt` (explicit appointment_id, else the
 *     same-patient-same-day inference; an inferred row says so)
 *   • CARRY-OVER — an earlier visit's piece that is overdue and still not in
 *     (`carryOverLabsForAppt`), shown first because it is the one to act on.
 *
 * `index` is optional: the patient page already holds one (pass it, no extra
 * request); /appointments does not, so the panel fetches this patient's labs itself.
 * Read-only on purpose — stage changes and edits stay on the lab surfaces, one door.
 * Renders nothing while loading and when there is no lab, like the flag.
 */
export default function ApptLabPanel({ appt, index: given }) {
  const me = useMe()
  const scope = labStageScope(me)
  const pid = Number(appt?.patient_id) || 0
  const [fetched, setFetched] = useState(null)
  const [stages, setStages] = useState([])

  useEffect(() => {
    if (given !== undefined || !pid) return
    let alive = true
    apiGet('/api/lab', { patient_id: pid, per_page: 200 })
      .then((r) => { if (alive) setFetched(buildApptLabIndex(asList(r))) })
      .catch(() => { if (alive) setFetched(null) })   // no panel beats a wrong one
    return () => { alive = false }
  }, [given, pid])

  // Only needed to merge LAB รับงาน + ทำงานเสร็จ into "อยู่ที่ LAB" for staff, so the
  // badge reads the same as StagePicker everywhere else. Fails soft to the raw stage.
  useEffect(() => {
    cachedGet('/api/labstage').then((r) => setStages(asList(r))).catch(() => {})
  }, [])

  const index = given !== undefined ? given : fetched
  if (!index || !appt?.id) return null
  const own = labsForAppt(index, appt)
  const carry = carryOverLabsForAppt(index, appt)
  if (!own.length && !carry.length) return null

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-400">
        <FlaskConical size={13} strokeWidth={2} />งาน Lab
        <span className="tabular-nums normal-case tracking-normal">· {own.length + carry.length} ชิ้น</span>
        {pid > 0 && (
          <a href={`/patient/${pid}/lab`} target="_blank" rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal text-[12.5px] font-medium text-brand-600 hover:text-brand-700">
            หน้า Lab ของคนไข้ <ExternalLink size={12} />
          </a>
        )}
      </div>
      <div className="space-y-2">
        {carry.map((r) => <LabRow key={`c${r.id}`} row={r} appt={appt} stages={stages} scope={scope} carry />)}
        {own.map((r) => <LabRow key={r.id} row={r} appt={appt} stages={stages} scope={scope} />)}
      </div>
    </div>
  )
}

function LabRow({ row, appt, stages, scope, carry = false }) {
  const chase = labChaseState(row)
  const tone = CHASE_TONES[chase.level] || CHASE_TONES.due
  const st = shownStage(row.labstage_id, stages, scope)
  const style = stageStyle(st?.color || labStageColor(row))
  const stageName = st?.name || labStageName(row) || '—'
  const img = labDoctorImg(row)
  const inferred = !carry && Number(row.appointment_id) !== Number(appt.id)
  const due = labDue(row)
  const ret = hasRetainer(row.retainer) ? retainerSummary(row.retainer) : ''

  return (
    <div className={`rounded-xl border p-3 ${carry ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200/80 bg-white'}`}>
      {carry && (
        <div className="flex items-center gap-1.5 mb-1.5 text-[12.5px] font-semibold text-rose-700">
          <AlertTriangle size={13} /> ค้างจากนัดก่อน — ควรได้รับก่อนนัดนี้
        </div>
      )}
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-medium text-slate-900 leading-snug break-words">{labTitle(row)}</div>
          {ret && ret !== labTitle(row) && <div className="text-[12.5px] text-slate-500 mt-0.5">{ret}</div>}
        </div>
        {row.repair && <Badge tone="amber" className="shrink-0"><Wrench size={10} /> แก้</Badge>}
        <span className="shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[12px] font-medium" style={style.badge}>
          {stageName}
        </span>
      </div>
      <div className="mt-2 text-[12.5px] text-slate-500 flex items-center gap-1.5 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded-full ring-1 ring-inset px-1.5 py-0.5 text-[11.5px] font-semibold ${tone.chip}`}>
          {chase.label}
        </span>
        <span className="truncate max-w-[160px]">{labVendorName(row)}</span>
        <span className="text-slate-300">·</span>
        <span className="inline-flex items-center gap-1">
          {img
            ? <img src={img} alt="" className="w-4 h-4 rounded-full object-cover bg-slate-100" />
            : <Stethoscope size={11} className="text-slate-400" />}
          {labDoctorCode(row)}
        </span>
        {due > 0 && <><span className="text-slate-300">·</span><span className="tabular-nums">ครบกำหนด {fmtDateText(due)}</span></>}
        {carry && Number(row.date) > 0 && <><span className="text-slate-300">·</span><span className="tabular-nums">นัดเดิม {fmtDateText(Number(row.date))}</span></>}
      </div>
      {inferred && (
        <div className="mt-1.5 text-[11.5px] text-slate-400">จับคู่จากวันที่นัด (Lab นี้ไม่ได้ผูกกับนัดหมายโดยตรง)</div>
      )}
    </div>
  )
}
